import { useQuery } from "@tanstack/react-query"
import {
  dashboardDateQuery,
  routes,
  type DashboardDateSelection,
  type MarketplaceId,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { keepPreviousForMarketplace } from "./marketplace-query"

/** One product's sales with the window's ad attribution taken out. Both figures
 *  are floored at zero — see `attributionExceededSales`. */
export type OrganicProductRow = {
  sku: string
  name: string | null
  imageUrl: string | null
  /** Catalog EAN, offered beside the SKU for copying. Null when the catalog has
   *  no row for this SKU in the marketplace's store. */
  ean: string | null
  /** Warehouse stock mirrored from Magento, already clamped at zero. Null means
   *  unknown — not zero — and the cell says so rather than claiming "sold out". */
  inventoryQty: number | null
  organicRevenue: number
  organicUnits: number
  /** The same figure over the previous window of equal length; the trend chip
   *  reads it, and it is 0 when the SKU sold nothing then. */
  previousOrganicRevenue: number
  /** The window attributed more to ads than it sold, so the figures are clamped
   *  rather than measured — the row says so instead of showing a negative. */
  attributionExceededSales: boolean
  /** The product's page on this marketplace. Null when the marketplace config
   *  carries no usable `productUrl` template — the cell then reads as a dash
   *  rather than as a dead link. */
  listingUrl: string | null
}

/** The columns the table can rank by. Ranking happens on the SERVER over every
 *  SKU that sold, so switching column refetches rather than re-sorting the ten
 *  rows already here — the leaders by units are a different set. */
export const ORGANIC_PRODUCT_SORTS = {
  revenue: "revenue",
  units: "units",
  trend: "trend",
} as const
export type OrganicProductSort = (typeof ORGANIC_PRODUCT_SORTS)[keyof typeof ORGANIC_PRODUCT_SORTS]
export type OrganicProductSortDirection = "asc" | "desc"

export type OrganicProductOrdering = {
  sort: OrganicProductSort
  direction: OrganicProductSortDirection
}

export const DEFAULT_ORGANIC_PRODUCT_ORDERING: OrganicProductOrdering = {
  sort: ORGANIC_PRODUCT_SORTS.revenue,
  direction: "desc",
}

export type OrganicProductsResponse = {
  marketplace: MarketplaceId
  windowStart: string
  windowEnd: string
  /** False when the marketplace filed no ad report covering the window, so
   *  every sale below is counted as organic without any subtraction. */
  adReported: boolean
  /** The ordering the rows were actually ranked by — the header marks its live
   *  column from this, not from local state a failed refetch could outrun. */
  sort: OrganicProductSort
  direction: OrganicProductSortDirection
  rows: OrganicProductRow[]
}

/** Top products by organic sales for one marketplace over the PAGE's period. A
 *  separate query from `useDashboard` (offer-health pattern) so the table loads
 *  on its own and a slow ad-fact aggregate never holds up the chart above it. */
export function useOrganicProducts(
  marketplace: MarketplaceId,
  selection: DashboardDateSelection,
  ordering: OrganicProductOrdering = DEFAULT_ORGANIC_PRODUCT_ORDERING
) {
  const query = {
    ...dashboardDateQuery(selection),
    sort: ordering.sort,
    dir: ordering.direction,
  }
  return useQuery({
    queryKey: ["organic-products", marketplace, query] as const,
    enabled: !!marketplace,
    // Same reasoning as the campaign table: a period change may keep this
    // shop's rows on screen, a marketplace change must not. It also keeps the
    // previous column's rows visible while the next ordering loads, so a sort
    // click does not blank the table under the cursor.
    placeholderData: keepPreviousForMarketplace(marketplace),
    queryFn: async () => {
      const result = await apiFetch<OrganicProductsResponse>(
        routes.marketplaces.organicProducts({ marketplace }),
        { query }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}
